import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map, catchError, combineLatest, EMPTY, filter, switchMap, take, tap } from 'rxjs';

import { SegmentTrackEvent } from '@ninety/platform/models/analytics/segment/segment-track-event.enum';
import { SegmentService } from '@ninety/platform/services/services/segment.service';
import { selectUserFlagsAreLoaded, selectCurrentUserAsCompanyUser } from '@ninety/platform/state';
import { SegmentActions } from '@ninety/platform/state/app-global/analytics/segment/segment.actions';
import { selectNotificationsEnabled } from '@ninety/platform/state/app-global/notifications/notification.selectors';
import { selectHasTier1Data } from '@ninety/platform/state/app-init/app-init.selectors';
import { NotificationsSettingsService } from '@ninety/web/pages/settings/user/notifications/services/notifications-settings.service';

import { buildNotificationTraits } from '../models/notification-traits';

import { NotificationFeedActions } from './notification-feed.actions';

@Injectable()
export class NotificationFeedAnalyticsEffects {
  private readonly segmentService = inject(SegmentService);
  private readonly notificationsSettingsService = inject(NotificationsSettingsService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  identifyNotificationSettings$ = createEffect(
    () =>
      combineLatest([
        this.store.select(selectCurrentUserAsCompanyUser).pipe(filter(cu => !!cu)),
        this.store.select(selectHasTier1Data),
        this.store.select(selectUserFlagsAreLoaded),
      ]).pipe(
        filter(([cu, hasTier1Data, flagsLoaded]) => !!cu?.company && hasTier1Data && flagsLoaded),
        take(1),
        concatLatestFrom(() => this.store.select(selectNotificationsEnabled)),
        filter(([_, notificationsEnabled]) => !!notificationsEnabled),
        switchMap(([[cu]]) =>
          this.notificationsSettingsService.getNotificationSettings(true).pipe(
            tap(feedNotifications => {
              const notificationTraits = buildNotificationTraits(feedNotifications);

              this.segmentService.identify({
                id: cu.personId,
                company: {
                  id: cu.company._id,
                },
                notifications: notificationTraits,
              });
            }),
            catchError(() => EMPTY)
          )
        )
      ),
    { dispatch: false }
  );

  trackOpeningNotificationFeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.open),
      map(() =>
        SegmentActions.track({
          event: SegmentTrackEvent.NOTIFICATION_FEED_OPENED,
        })
      )
    )
  );

  trackNotificationClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.openItemDetail),
      map(({ notification }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.NOTIFICATION_CLICKED,
          params: {
            notificationId: notification.id,
            itemType: notification.itemType,
            itemId: notification.itemId,
            reason: notification.reason,
            isRead: !!notification.markedReadAt,
            teamId: notification.teamId,
          },
        })
      )
    )
  );

  trackNotificationReadStatusToggled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.toggleNotificationReadStatus),
      map(({ notification }) => {
        const newStatus = !notification.markedReadAt;
        return SegmentActions.track({
          event: SegmentTrackEvent.NOTIFICATION_READ_STATUS_TOGGLED,
          params: {
            notificationId: notification.id,
            itemType: notification.itemType,
            itemId: notification.itemId,
            reason: notification.reason,
            action: newStatus ? 'mark_as_read' : 'mark_as_unread',
            teamId: notification.teamId,
            isRead: newStatus,
          },
        });
      })
    )
  );

  trackNotificationFeedFilterChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.setFilterOption),
      map(({ filterBy }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.NOTIFICATION_FEED_FILTER_CHANGED,
          params: {
            filter: filterBy,
          },
        })
      )
    )
  );

  trackNotificationMarkAllAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationFeedActions.markAllAsRead),
      map(() =>
        SegmentActions.track({
          event: SegmentTrackEvent.NOTIFICATION_FEED_MARK_ALL_AS_READ,
        })
      )
    )
  );
}
